
  import { AccountModule, AccountStore } from '@/store/account';
  import { ref } from 'vue';
  import { Options, Vue } from 'vue-class-component';
  import { FilterMatchMode, FilterService } from 'primevue/api';
  import CreateUpdateAccount from './CreateUpdateAccount.vue';
  import ModalComponent from '../../ModalComponent.vue';
  import { ExportAccountsCommand, UserDto, ApplicationRoleType } from '@/apiclient/apiclient_generated';
  import FileService from '@/services/file.service';
  import Routes from '@/types/Routes';
  import { NotificationService } from '@/services/notification-service';
  import useEventsBus from '@/utils/eventBus';
  import AppEvents from '@/types/AppEvents';
  import PushToPipedrive from './PushToPipedrive.vue';

  @Options({
    components: {
      CreateUpdateAccount,
      ModalComponent,
      PushToPipedrive
    }
  })
  export default class AccountManagement extends Vue {
    private accountStore: AccountModule = AccountStore;

    private rows = 25;
    private loading = false;
    showModal = false;
    showPipedriveModal = false;
    private account = {} as UserDto;

    globalFilter = ['company', 'name', 'surname', 'email'];

    roleFilterOptions = ['Administrator', 'Employee', 'Customer', 'NewsletterRecipient'];

    selectedAccount: any = ref();

    get totalRecords() {
      return this.items.length;
    }

    rowClass() {
      return 'cursor-pointer';
    }

    get items() {
      return this.accountStore.allAccounts;
    }

    get deleteLabel() {
      return 'Delete Account';
    }

    emailConfirmedOptions = [
      { label: 'Yes', value: true },
      { label: 'No', value: false }
    ];

    filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      company: { value: null, matchMode: FilterMatchMode.CONTAINS },
      salutation: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      surname: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: { value: null, matchMode: FilterMatchMode.CONTAINS },
      emailConfirmed: { value: null, matchMode: FilterMatchMode.IN },
      role: { value: null, matchMode: FilterMatchMode.IN },
      createdDate: { value: null, matchMode: 'dateRange' }
    });

    private sanitizeTableState(stateKey: string, defaultRows = 25) {
      try {
        const raw = localStorage.getItem(stateKey);
        if (raw) {
          const state = JSON.parse(raw);
          if (!state.rows || state.rows <= 0) {
            state.rows = defaultRows;
          }
          // Always clear stored filters — the component owns the filter schema.
          // A stale schema from before filter columns were added prevents filters from applying.
          delete state.filters;
          localStorage.setItem(stateKey, JSON.stringify(state));
        }
      } catch {
        localStorage.removeItem(stateKey);
      }
    }

    beforeMount() {
      FilterService.register('dateRange', (value: Date, filter: Date[]) => {
        if (!filter || filter.length === 0) return true;
        const [from, to] = filter;
        if (!from) return true;
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        const fromDate = new Date(from);
        fromDate.setHours(0, 0, 0, 0);
        if (!to) return date >= fromDate;
        const toDate = new Date(to);
        toDate.setHours(23, 59, 59, 999);
        return date >= fromDate && date <= toDate;
      });
      this.sanitizeTableState('accounts-table-state');
    }

    async mounted() {
      this.resetSelectedAccountsArray();

      await this.accountStore.getAllUsers();

      this.initSearchValue();

      if (this.items.length) {
        this.selectedAccount = this.items[0];
      }

      const { bus } = useEventsBus();
      this.$watch(
        () => bus.value.get(AppEvents.ModifyCurrentUserAcc),
        () => {
          this._modifyAccount();
        }
      );

      if (this.accountStore.modifyAccountMode) {
        this.accountStore.setModifyAccountMode(false);
        this._modifyAccount();
      }
    }

    private _modifyAccount() {
      const userId = this.accountStore.userData.id;
      const user = this.items.find((t) => t.id == userId);
      this.modifyAccountFromGrid(user);
    }

    unmounted() {
      this.accountStore.setAccounts([]);
    }

    createAccount() {
      this.account = {} as UserDto;
      this.showModal = true;
    }

    async exportAccount(account) {
      const exportCommand = {
        customers: [account.id]
      } as ExportAccountsCommand;

      await this._exportAccounts(exportCommand);
    }

    async exportAccounts() {
      const exportCommand = {} as ExportAccountsCommand;

      await this._exportAccounts(exportCommand);
    }

    private async _exportAccounts(exportCommand: ExportAccountsCommand) {
      const file = await this.accountStore.exportAllUsers(exportCommand);

      if (file) {
        FileService.downloadFile(file.data, file.fileName);
      }
    }

    pushToPipedrive() {
      this.showPipedriveModal = true;
    }

    initSearchValue() {
      this.filters['global'].value = this.accountStore.accountSearchValue;
    }

    saveSearchValue() {
      this.accountStore.setAccountSearchValue(this.filters['global'].value);
    }

    navigateToLicense(accountId) {
      this.saveSearchValue();

      this.$router.push({ path: Routes.management.licenses.path, query: { account: accountId } });
    }

    accountDblClicked(row) {
      if (this.accountStore.isCustomer) {
        return;
      }

      this.saveSearchValue();

      const accountId = row.data.id;
      this.navigateToLicense(accountId);
    }

    manageLicenses() {
      const accountId = this.selectedAccount.id;
      this.$router.push({ path: Routes.management.licenses.path, query: { account: accountId } });
    }

    async deleteAccount(event) {
      const targetAccountId = this.selectedAccount.id;

      this.$confirm.require({
        target: event.currentTarget,
        message: 'Are you sure you want to delete selected account?',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          const deleteResult = await this.accountStore.deleteAccountAction(targetAccountId);
          if (deleteResult) {
            this.resetSelectedAccountsArray();
          }
        },
        reject: () => {
          //callback to execute when user rejects the action
        }
      });
    }

    get invalidAccountsFormToDelete() {
      return this.selectedAccount.id == this.accountStore.userData.id;
    }
    invalidAccountToDeleteFromGrid(account) {
      return account.id == this.accountStore.userData.id;
    }

    modifyAccountFromGrid(account) {
      this.selectedAccount = account;

      this.modifyAccount();
    }

    async deleteAccountFromGrid(account, $event) {
      this.selectedAccount = account;

      await this.deleteAccount($event);
    }

    modifyAccount() {
      this.account = this.selectedAccount;

      this.showModal = true;
    }

    private closeCreateUpdateModal() {
      this.showModal = false;
      this.account = {} as UserDto;
    }

    private _closeModals() {
      this.showModal = false;
      this.resetSelectedAccountsArray();
    }

    async processUpdateAccount() {
      this._closeModals();
      NotificationService.NotifySuccess('Account was successfully updated.');
    }

    async processReactivateAccount(createdAccount) {
      this._closeModals();
      NotificationService.NotifySuccess('Account was successfully reactivated.');
      this.navigateToLicense(createdAccount.id);
    }

    processCreateAccount(createdAccount) {
      this._closeModals();
      NotificationService.NotifySuccess('Account was successfully created.');
      this.navigateToLicense(createdAccount.id);
    }

    processCancel() {
      this.showModal = false;
    }

    resetSelectedAccountsArray() {
      this.selectedAccount = [];
    }
  }
